import type { Market } from "../domain/location/types";
import type { SaunaType } from "../types/design";

export const CONVERSION_EVENT_NAMES = [
	"funnel_wizard_started",
	"funnel_wizard_step_completed",
	"funnel_wizard_completed",
	"funnel_design_valid",
	"funnel_design_saved",
	"funnel_pdf_exported",
	"funnel_quote_opened",
	"funnel_cart_clicked",
	"funnel_build_started",
	"funnel_contractor_lead_started",
	"funnel_contractor_lead_completed",
	"funnel_contractor_lead_failed",
	"guide_opened",
	"guide_checkpoint_completed",
	"guide_gate_completed",
] as const;

export type ConversionEventName = (typeof CONVERSION_EVENT_NAMES)[number];
export type ViewportClass = "mobile" | "tablet" | "desktop";

type TelemetryScalar = string | number | boolean;

export interface ConversionEventProperties {
	market?: Market;
	sauna_type?: SaunaType;
	viewport_class?: ViewportClass;
	wizard_step?: number;
	guide_surface?: "desktop" | "mobile";
	guide_step_id?: string;
	guide_checkpoint_id?: string;
	failure_kind?: "technical";
}

type SimpleAnalyticsEvent = (
	event: string,
	metadata?: Record<string, TelemetryScalar>,
) => void;

declare global {
	interface Window {
		sa_event?: SimpleAnalyticsEvent & { q?: unknown[][] };
		sa_loaded?: boolean;
	}
}

const allowedPropertyKeys = new Set([
	"market",
	"sauna_type",
	"viewport_class",
	"funnel_session",
	"elapsed_ms",
	"wizard_step",
	"guide_surface",
	"guide_step_id",
	"guide_checkpoint_id",
	"failure_kind",
]);
const sentMilestones = new Set<string>();
let activeSession: { id: string; startedAt: number } | null = null;

function randomSessionId(): string {
	try {
		return crypto.randomUUID();
	} catch {
		return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
	}
}

function ensureSession(): { id: string; startedAt: number } {
	activeSession ??= { id: randomSessionId(), startedAt: Date.now() };
	return activeSession;
}

export function beginConversionFunnel(): void {
	activeSession = { id: randomSessionId(), startedAt: Date.now() };
}

export function getViewportClass(): ViewportClass {
	if (typeof window === "undefined") return "desktop";
	if (window.innerWidth < 768) return "mobile";
	if (window.innerWidth < 1100) return "tablet";
	return "desktop";
}

function sanitizeProperties(
	properties: Record<string, unknown>,
): Record<string, TelemetryScalar> {
	const safe: Record<string, TelemetryScalar> = {};
	for (const [key, value] of Object.entries(properties)) {
		if (!allowedPropertyKeys.has(key)) continue;
		if (
			typeof value !== "string" &&
			typeof value !== "number" &&
			typeof value !== "boolean"
		) {
			continue;
		}
		if (typeof value === "number" && !Number.isFinite(value)) continue;
		safe[key] = value;
	}
	return safe;
}

export function trackConversionEvent(
	event: ConversionEventName,
	properties: ConversionEventProperties = {},
	options: { once?: string } = {},
): void {
	if (typeof window === "undefined") return;

	try {
		const session = ensureSession();
		const milestoneKey = options.once
			? `${session.id}:${options.once}`
			: null;
		if (milestoneKey && sentMilestones.has(milestoneKey)) return;

		const emitter = window.sa_event;
		if (typeof emitter !== "function") return;

		const metadata = sanitizeProperties({
			...properties,
			market: properties.market,
			sauna_type: properties.sauna_type,
			viewport_class: properties.viewport_class ?? getViewportClass(),
			funnel_session: session.id,
			elapsed_ms: Math.max(0, Date.now() - session.startedAt),
		});
		emitter(event, metadata);
		if (milestoneKey) sentMilestones.add(milestoneKey);
	} catch {
		// Analytics is deliberately outside the product's success path.
	}
}

export function installSimpleAnalyticsEventQueue(): void {
	if (typeof window === "undefined" || typeof window.sa_event === "function") {
		return;
	}

	const queued: SimpleAnalyticsEvent & { q?: unknown[][] } = (...args) => {
		(queued.q ??= []).push(args);
	};
	window.sa_event = queued;
}

export function resetConversionTelemetryForTests(): void {
	sentMilestones.clear();
	activeSession = null;
}
